/* ==========================================================================
   Roché FAQ — accordion behaviour
   - smooth open / close animation (Web Animations API)
   - optional single-open per section
   - optional live search across every FAQ section on the page
   - optional jump links built from every FAQ section on the page
   - one FAQPage rich-result blob per page
   ========================================================================== */

(function () {
  'use strict';

  var EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
  var DURATION = 380;

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  class RocheFaq extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.roche-faq__item'));
      this.singleOpen = this.dataset.singleOpen === 'true';
      this.animations = new WeakMap();

      this.items.forEach(function (item) {
        var summary = item.querySelector('.roche-faq__question');
        if (!summary) return;
        summary.addEventListener('click', this.onSummaryClick.bind(this, item));
      }, this);

      this.setupSearch();
      RocheFaq.setupPage();
      this.openFromHash();
    }

    onSummaryClick(item, event) {
      event.preventDefault();
      if (item.open) {
        this.closeItem(item);
      } else {
        if (this.singleOpen) {
          this.items.forEach(function (other) {
            if (other !== item && other.open) this.closeItem(other);
          }, this);
        }
        this.openItem(item);
      }
    }

    openItem(item) {
      var answer = item.querySelector('.roche-faq__answer');
      if (!answer) return;

      this.cancelAnimation(answer);
      item.open = true;

      if (prefersReducedMotion()) {
        answer.style.height = '';
        return;
      }

      var target = answer.scrollHeight;
      var animation = answer.animate(
        [
          { height: '0px', opacity: 0 },
          { height: target + 'px', opacity: 1 }
        ],
        { duration: DURATION, easing: EASE }
      );

      this.animations.set(answer, animation);
      animation.onfinish = function () {
        answer.style.height = '';
      };
    }

    closeItem(item) {
      var answer = item.querySelector('.roche-faq__answer');
      if (!answer) return;

      this.cancelAnimation(answer);

      if (prefersReducedMotion()) {
        item.open = false;
        return;
      }

      var start = answer.scrollHeight;
      var animation = answer.animate(
        [
          { height: start + 'px', opacity: 1 },
          { height: '0px', opacity: 0 }
        ],
        { duration: DURATION - 60, easing: EASE }
      );

      this.animations.set(answer, animation);
      animation.onfinish = function () {
        item.open = false;
        answer.style.height = '';
      };
    }

    cancelAnimation(answer) {
      var running = this.animations.get(answer);
      if (running) running.cancel();
    }

    /* ---------- deep link: /pages/faqs#faq-how-do-i-wash ---------- */
    openFromHash() {
      if (!window.location.hash) return;
      var target = this.querySelector(window.location.hash);
      if (!target || !target.classList.contains('roche-faq__item')) return;
      target.open = true;
      window.requestAnimationFrame(function () {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    /* ---------- search ---------- */
    setupSearch() {
      var input = this.querySelector('.roche-faq__search-input');
      if (!input) return;

      this.classList.add('roche-faq--search-host');

      var timer = null;
      input.addEventListener('input', function () {
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          RocheFaq.filter(input.value);
        }, 120);
      });
    }

    static filter(term) {
      var query = (term || '').trim().toLowerCase();
      var sections = document.querySelectorAll('.roche-faq');
      var totalMatches = 0;

      sections.forEach(function (section) {
        var matches = 0;

        section.querySelectorAll('.roche-faq__item').forEach(function (item) {
          var haystack = (item.dataset.search || '').toLowerCase();
          var hit = query === '' || haystack.indexOf(query) !== -1;

          item.hidden = !hit;
          item.classList.toggle('is-match', hit && query !== '');
          if (hit) matches += 1;
        });

        section.classList.toggle('is-empty', matches === 0);
        totalMatches += matches;
      });

      var nav = document.querySelector('.roche-faq__nav');
      if (nav) nav.hidden = query !== '';

      return totalMatches;
    }

    /* ---------- page-level extras: jump links + rich results ---------- */
    static setupPage() {
      if (RocheFaq.pageReady) return;
      RocheFaq.pageReady = true;

      window.requestAnimationFrame(function () {
        RocheFaq.buildNav();
        RocheFaq.buildSchema();
      });
    }

    static buildNav() {
      var nav = document.querySelector('.roche-faq__nav');
      if (!nav) return;

      var sections = document.querySelectorAll('.roche-faq');
      var html = '';

      sections.forEach(function (section) {
        var label = section.dataset.category;
        if (!label) {
          var title = section.querySelector('.roche-faq__title');
          label = title ? title.textContent.trim() : '';
        }
        if (!label) return;
        html +=
          '<li><a class="roche-faq__nav-link" href="#' +
          section.id +
          '">' +
          label +
          '</a></li>';
      });

      nav.innerHTML = html;
      nav.hidden = html === '';
    }

    static buildSchema() {
      var host = document.querySelector('.roche-faq[data-faq-schema="true"]');
      if (!host || document.getElementById('roche-faq-schema')) return;

      var entities = [];

      document.querySelectorAll('.roche-faq__item').forEach(function (item) {
        var question = item.querySelector('.roche-faq__question-text');
        var answer = item.querySelector('.roche-faq__answer-inner');
        if (!question || !answer) return;

        entities.push({
          '@type': 'Question',
          name: question.textContent.trim(),
          acceptedAnswer: {
            '@type': 'Answer',
            text: answer.innerHTML.trim()
          }
        });
      });

      if (!entities.length) return;

      var script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'roche-faq-schema';
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: entities
      });
      document.head.appendChild(script);
    }
  }

  RocheFaq.pageReady = false;

  if (!customElements.get('roche-faq')) {
    customElements.define('roche-faq', RocheFaq);
  }

  /* Theme editor: rebuild page-level extras when a section is added or moved */
  document.addEventListener('shopify:section:load', function () {
    RocheFaq.pageReady = false;
    RocheFaq.setupPage();
  });
})();
